'use client';

import React from 'react';
import { motion } from 'framer-motion';
import Script from 'next/script';
import SectionBase from '@/components/ui/sections/section-base';
import SectionHeader from '@/components/ui/sections/section-header';
import { TypographyH4, TypographySmall } from '@/components/ui/typography';
import { cls } from '@/lib/utils';

const SECTION_ID = 'service-areas-faq';

const wrapperMotionProps = {
  initial: 'hidden' as const,
  whileInView: 'visible' as const,
  viewport: { once: true },
  variants: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 },
    },
  },
};

const itemMotionProps = {
  variants: {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  },
};

const FAQS = [
  {
    question: 'What areas does Cornerstone Homes serve in Oklahoma?',
    answer:
      'Cornerstone Homes & Properties serves communities throughout northeastern Oklahoma, including Claremore, Catoosa, Verdigris, Inola, Owasso, Broken Arrow, Tulsa, and the broader Rogers County area. We also build in parts of Tulsa County, Wagoner County, and Mayes County.',
  },
  {
    question: 'How much does it cost to build a custom home in northeastern Oklahoma?',
    answer:
      'Custom home costs vary based on size, finishes, lot conditions, and location. Northeastern Oklahoma generally offers more affordable land and construction costs compared to many metropolitan areas. Contact us for a personalized estimate based on your specific plans and property.',
  },
  {
    question: 'Does Cornerstone Homes help with finding land or lots?',
    answer:
      'While we are not a real estate agency, we regularly advise clients on lot selection and can evaluate properties for buildability, including soil conditions, utility access, well and septic feasibility, and zoning compatibility with your intended home design.',
  },
  {
    question: 'What is the design-and-build process?',
    answer:
      'Our design-and-build model means you work with one team from initial concept through construction completion. We handle architectural design, engineering, permitting, and construction in-house, ensuring seamless communication and accountability at every stage.',
  },
  {
    question: 'Can I build on rural acreage outside city limits?',
    answer:
      'Yes. We frequently build on rural acreage throughout Rogers County and surrounding areas. We have extensive experience with well and septic systems, rural water districts, county permitting, and the unique site preparation requirements of rural properties.',
  },
  {
    question: 'Does Cornerstone handle building permits and inspections?',
    answer:
      'Absolutely. We manage all permit applications, plan submissions, and coordinate every required inspection. Our team is experienced with permitting procedures across all the jurisdictions in our service area, from city building departments to county offices.',
  },
  {
    question: 'What types of projects does Cornerstone Homes build?',
    answer:
      'We build custom homes, room additions, barns and outbuildings, garages, basements, and screened-in porches. We also perform whole-home renovations and kitchen remodels. Whether it is a ground-up build or a transformation of an existing property, we can help.',
  },
  {
    question: 'How long does it take to build a custom home in Oklahoma?',
    answer:
      'A typical custom home build takes between 8 and 14 months from groundbreaking to move-in, depending on the size, complexity, and any custom features. We provide a detailed construction timeline during the planning phase so you know what to expect.',
  },
  {
    question: 'Why choose Cornerstone Homes over a national production builder?',
    answer:
      'Cornerstone is locally owned and operated in Claremore with over 20 years of experience. We offer truly custom designs, transparent fixed-price contracts, in-house design services, and a level of personal attention that large production builders simply cannot match.',
  },
  {
    question: 'How do I get started with Cornerstone Homes?',
    answer:
      'Getting started is easy. Call us at 918-344-1731, visit our Claremore office, or fill out the consultation request form on our website. We will schedule a meeting to discuss your vision, walk your property if applicable, and outline next steps.',
  },
];

function ServiceAreasFAQSection() {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <Script
        id="faq-schema-service-areas"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <SectionBase id={SECTION_ID} background="bg-background">
        <motion.div
          className={cls`flex flex-col items-center gap-10`}
          {...wrapperMotionProps}
        >
          <SectionHeader
            subtitle="FAQ"
            title="Frequently Asked Questions"
            description="Common questions about building and renovating homes across our northeastern Oklahoma service area."
          />

          <div className={cls`flex flex-col gap-6 w-full max-w-3xl mx-auto`}>
            {FAQS.map((faq) => (
              <motion.div
                key={faq.question}
                className={cls`flex flex-col gap-3 p-6 rounded-lg border border-border bg-card`}
                {...itemMotionProps}
              >
                <TypographyH4 className="text-foreground">
                  {faq.question}
                </TypographyH4>
                <TypographySmall className="text-muted-foreground leading-relaxed">
                  {faq.answer}
                </TypographySmall>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </SectionBase>
    </>
  );
}

export default ServiceAreasFAQSection;
